<template>
  <NuxtLayout>
    <template #header-title>
      <div
        class="edit-job-mobile-header"
        role="button"
        tabindex="0"
        aria-label="Go back to manage jobs"
        data-cy="edit-job-back-header"
        @click="redirectBasedOnStatus()"
      >
        <span class="edit-job-mobile-header__back">
          <ui-icon
            type="round"
            size="24"
          >
            arrow_back
          </ui-icon>
        </span>
        <span class="edit-job-mobile-header__title">
          {{ vehicleTitle }}
        </span>
        <span class="edit-job-mobile-header__spacer" />
      </div>
    </template>

    <AcvDialog
      :disableBodyScrolling="false"
      :showDialog="isCancelDialogOpen"
      :submitText="dialogSubmitText"
      :cancelText="dialogCancelText"
      :title="dialogTitle"
      slide
      centered
      shouldFocus
      @closed="closeDialog"
      @canceled="closeDialog"
      @submitted="onDialogAccept"
      class="dialog--cancel"
    >
      <template #body>
        <template v-if="isCancelableBySelectedStatus">
          <p class="tw-m-0">This will not impact your Carrier Performance Score.</p>
        </template>
        <template v-else>
          <p class="tw-mb-4 tw-mt-0">Cancellations Impact:</p>
          <p class="tw-my-0 tw-leading-5">Your Carrier Performance Score.</p>
          <p class="tw-my-0 tw-leading-5">Eligibility in the Preferred Carrier Program.</p>
          <p class="tw-my-0 tw-leading-5">The number of jobs you can stage and book at one time.</p>
        </template>
      </template>
    </AcvDialog>

    <AcvDialog
      :disableBodyScrolling="false"
      :showDialog="isETADialogVisible"
      submitText="Update"
      slide
      title="Update delivery date?"
      centered
      shouldFocus
      @closed="closeETADialog"
      @canceled="closeETADialog"
      @submitted="handleETAUpdate"
    >
      <template #body>
        {{ ETADialogMessage }}
      </template>
    </AcvDialog>

    <UISideSheet
      @toggleSideSheet="toggleNotes"
      :isOpen="isNotesSidebarOpen"
      @onActionClick="onSaveNotesClick"
      :title="'Notes'"
      :action-text="'Save Note'"
    >
      <FormTextarea
        class="notes-textarea"
        placeholder="Add a note"
        :value="selectedJob?.carrierComment"
        @on-text-entered="handleTextEntered"
      />
    </UISideSheet>
    <UISideSheet
      v-if="!isCancelableBySelectedStatus"
      @toggleSideSheet="toggleCancelOverlay"
      @onActionClick="openDialog"
      :isOpen="isCancelJobSidebarOpen"
      :isDisabled="isCancelFormDisabled"
      :title="'Cancel Job'"
      :action-text="'Cancel Job'"
      action-variant="error"
    >
      <FormDropdown
        :options="cancelJobOptions"
        @selected="onCancelReasonSelected"
        data-test-id="cancel-reason"
        class="tw-mb-6"
      />
      <FormTextarea
        v-if="!isCancelableBySelectedStatus"
        placeholder="Add a note (Required)"
        class="notes-textarea"
        @on-text-entered="handleCancelTextEntered"
        data-test-id="cancel-notes"
      />
    </UISideSheet>

    <div class="template__body">
      <div class="home__container">
        <div
          class="home__loading-icon"
          v-if="isLoading"
          data-testid="home__loading-icon"
        >
          <AcvProgressIndicator active />
          <h5 class="home__loading-text">Finding job...</h5>
        </div>
        <LazyJobEditManagePageContent v-else-if="shouldShowLegacyLayout" />
        <LazyJobEditManagePageContentImprovement v-else-if="shouldShowMobileLayout" />
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
import { computed, provide } from 'vue';
import { featureFlagSlugs } from '@/constants/featureFlagSlugs';
import { useFeatureFlagsStore } from '@/stores/useFeatureFlags';
import { AcvDialog } from '@acv/shared-vue';
import { MANAGE_JOB_EDIT_PAGE_KEY, useManageJobEditPage } from '@/composables/useManageJobEditPage';

definePageMeta({
  middleware: ['edit-job'],
});

const page = useManageJobEditPage();
provide(MANAGE_JOB_EDIT_PAGE_KEY, page);

const {
  isLoading,
  requestId,
  isCancelDialogOpen,
  dialogSubmitText,
  dialogCancelText,
  dialogTitle,
  closeDialog,
  onDialogAccept,
  isCancelableBySelectedStatus,
  isETADialogVisible,
  closeETADialog,
  handleETAUpdate,
  ETADialogMessage,
  toggleNotes,
  isNotesSidebarOpen,
  onSaveNotesClick,
  selectedJob,
  handleTextEntered,
  toggleCancelOverlay,
  openDialog,
  isCancelJobSidebarOpen,
  isCancelFormDisabled,
  onCancelReasonSelected,
  handleCancelTextEntered,
  cancelJobOptions,
  redirectBasedOnStatus,
} = page;

const vehicleTitle = computed(() => concatenateVehicleInfo(selectedJob.value?.vehicle));

const featureFlagsStore = useFeatureFlagsStore();
const isLoadBoardMobileImprovementManageEnabled = computed(() =>
  featureFlagsStore.getByFlagName(featureFlagSlugs.mobileImprovementManage),
);

const shouldShowLegacyLayout = computed(
  () => Boolean(requestId.value) && !isLoadBoardMobileImprovementManageEnabled.value,
);

const shouldShowMobileLayout = computed(
  () => Boolean(requestId.value) && Boolean(isLoadBoardMobileImprovementManageEnabled.value),
);
</script>

<style lang="scss" scoped>
@use '@/assets/scss/container.scss';

.edit-job-mobile-header {
  @apply tw-grid tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-px-1;
  grid-template-columns: 48px 1fr 48px;

  &__back {
    @apply tw-flex tw-size-12 tw-items-center tw-justify-center tw-rounded-full tw-p-1;
  }

  &__title {
    @apply tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-center tw-text-xl tw-font-medium tw-leading-[30px] tw-tracking-[0.15px] tw-text-text-primary;
  }

  &__spacer {
    @apply tw-size-12;
  }
}

.notes-textarea :deep(.mdc-text-field__input) {
  @apply tw-min-h-28;
}

.dialog--cancel {
  @apply tw-text-text-secondary;
  :deep() {
    &.centered {
      .acv-dialog-content {
        @apply tw-w-auto tw-max-w-max tw-rounded-2xl;
      }
    }

    .acv-dialog-content__header--title {
      @apply tw-text-xl tw-font-medium tw-text-black;
    }

    .acv-dialog-content__footer {
      @apply tw-gap-2;

      .acv-button.acv-dialog-button-cancel {
        @apply tw-mr-0 tw-rounded-full tw-border-0 tw-bg-transparent tw-text-secondary-base;
      }

      .acv-button:not(.acv-dialog-button-cancel) {
        @apply tw-rounded-full tw-border-0 tw-bg-error-dark tw-text-white;
        background-image: none;
      }
    }
  }
}
</style>
